// Prompt 18 — Admin-only Diagnostics page.
// Shows: QuickBooks CorePlus read-call meter, Environment card
// (no secret values), outbound hosting IP, and the QuickBooks API
// log with error filter + CSV export. Non-admins get the "Super
// admin only" message from the API 403.
import { useCallback, useEffect, useState } from "react";
import { Download, RefreshCw, ShieldCheck, Server, Globe, Activity } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import DiagnosticsSecurity from "../components/DiagnosticsSecurity";

export default function Diagnostics() {
  const [env, setEnv] = useState(null);
  const [hosting, setHosting] = useState(null);
  const [stripeDiag, setStripeDiag] = useState(null);
  const [billCos, setBillCos] = useState(null);
  const [usage, setUsage] = useState(null);
  const [log, setLog] = useState([]);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [companyFilter, setCompanyFilter] = useState("");
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const [e, h, u] = await Promise.all([
        api.get("/admin/diagnostics/environment"),
        api.get("/admin/diagnostics/hosting"),
        api.get("/admin/diagnostics/qbo-usage"),
      ]);
      setEnv(e.data); setHosting(h.data); setUsage(u.data);
      api.get("/admin/diagnostics/stripe").then((r) => setStripeDiag(r.data))
        .catch(() => setStripeDiag({ error: true }));
      api.get("/admin/diagnostics/billing-companies").then((r) => setBillCos(r.data))
        .catch(() => setBillCos({ companies: [], error: true }));
    } catch (ex) {
      setNotice({ kind: "error",
                  text: ex?.response?.data?.detail || "Could not load diagnostics." });
    }
  }, []);

  const loadLog = useCallback(async () => {
    const params = new URLSearchParams();
    if (errorsOnly) params.set("errors_only", "true");
    if (companyFilter.trim()) params.set("company_id", companyFilter.trim());
    const r = await api.get(`/admin/diagnostics/qbo-log?${params.toString()}`);
    setLog(r.data?.rows || []);
  }, [errorsOnly, companyFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLog(); }, [loadLog]);

  const downloadCsv = () => {
    const params = new URLSearchParams();
    if (errorsOnly) params.set("errors_only", "true");
    if (companyFilter.trim()) params.set("company_id", companyFilter.trim());
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const a = document.createElement("a");
    a.href = `${base}/api/admin/diagnostics/qbo-log.csv?${params.toString()}`;
    a.download = "";
    a.click();
  };

  const readPct = usage && usage.budget
    ? Math.round((usage.read_calls / usage.budget) * 100)
    : 0;

  return (
    <Layout>
      <PageHeader title="Diagnostics"
                  subtitle="Admin · QuickBooks log, environment, hosting" />
      <div className="space-y-6" data-testid="diagnostics-page">
        {notice ? (
          <div className={`card-flat p-4 text-sm ${notice.kind === "error" ? "text-red-700" : "text-emerald-700"}`}
               data-testid="diag-notice">{notice.text}</div>
        ) : null}

        {/* ---- QuickBooks usage ---- */}
        <section className="card-flat p-6" data-testid="diag-qbo-usage-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="label-mini flex items-center gap-2">
                <Activity size={12} /> quickbooks · coreplus read calls
              </div>
              <h2 className="font-display text-xl font-semibold mt-1">
                QuickBooks read calls this month:{" "}
                <span data-testid="qbo-reads-value">
                  {usage ? `${usage.read_calls.toLocaleString()} / ${usage.budget.toLocaleString()}`
                         : "—"}
                </span>
              </h2>
              <p className="text-sm text-neutral-600 mt-1">
                Writes this month: {usage?.write_calls?.toLocaleString() ?? "—"}
                {usage ? ` · ${usage.year_month}` : ""}
              </p>
            </div>
            <button className="btn-ghost flex items-center gap-2 text-xs"
                    onClick={() => { load(); loadLog(); }}>
              <RefreshCw size={12} /> refresh
            </button>
          </div>
          <div className="mt-4 h-2 bg-neutral-100 rounded">
            <div className={`h-2 rounded ${readPct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                 style={{ width: `${Math.min(readPct, 100)}%` }} />
          </div>
        </section>

        {/* ---- Environment ---- */}
        <section className="card-flat p-6" data-testid="diag-env-card">
          <div className="label-mini flex items-center gap-2">
            <ShieldCheck size={12} /> environment
          </div>
          <h2 className="font-display text-xl font-semibold mt-1">Config</h2>
          {env ? (
            <div className="mt-4 text-sm space-y-3">
              <KV k="app environment" v={env.app_environment} />
              <KV k="PUBLIC_APP_URL" v={env.public_app_url || "—"} />
              <KV k="CORS origins" v={(env.cors_origins || []).join(", ")} />
              <KV k="cookie domain" v={env.cookie_domain} />
              <KV k="secure cookies" v={String(env.secure_cookies)} />
              <div className="pt-2">
                <div className="label-mini">per-GL</div>
                <div className="mt-2 border border-neutral-200">
                  {env.gls.map((g) => (
                    <div key={g.gl} className="px-4 py-3 border-b border-neutral-100 last:border-0"
                         data-testid={`diag-gl-${g.gl}`}>
                      <div className="font-medium">{g.gl}</div>
                      <div className="text-xs text-neutral-600 mt-1 font-mono">
                        env: {g.environment || g.credential_set || "—"}
                        {g.bc_environment_name ? ` · bc_name: ${g.bc_environment_name}` : ""}
                        {g.minor_version ? ` · minorversion: ${g.minor_version}` : ""}
                      </div>
                      <div className="text-xs text-neutral-600 font-mono">redirect: {g.redirect_uri || "—"}</div>
                      {g.api_host ? (
                        <div className="text-xs text-neutral-600 font-mono">api: {g.api_host}</div>
                      ) : null}
                      {g.gl === "odoo" ? (
                        <div className="text-xs mt-1" data-testid="diag-gl-odoo-auth">
                          auth: per-company API key (stored encrypted)
                          {" · "}token_encryption_key <span className={g.token_encryption_key === "set" ? "text-emerald-700" : "text-amber-700"}>{g.token_encryption_key}</span>
                        </div>
                      ) : (
                      <div className="text-xs mt-1">
                        client_id <span className={g.client_id === "set" ? "text-emerald-700" : "text-amber-700"}>{g.client_id}</span>
                        {" · "}client_secret <span className={g.client_secret === "set" ? "text-emerald-700" : "text-amber-700"}>{g.client_secret}</span>
                        {g.tenant_id ? <> {" · "}tenant_id <span className={g.tenant_id === "set" ? "text-emerald-700" : "text-amber-700"}>{g.tenant_id}</span></> : null}
                      </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : <div className="text-sm text-neutral-500 mt-4">Loading…</div>}
        </section>

        {/* ---- Stripe ---- */}
        <section className="card-flat p-6" data-testid="diag-stripe-card">
          <div className="label-mini flex items-center gap-2">
            <ShieldCheck size={12} /> billing
          </div>
          <h2 className="font-display text-xl font-semibold mt-1">Stripe</h2>
          {!stripeDiag ? <div className="text-sm text-neutral-500 mt-4">Loading…</div>
            : stripeDiag.error ? <div className="text-sm text-red-700 mt-4" data-testid="diag-stripe-error">Could not load Stripe diagnostics.</div>
            : <StripeBlock d={stripeDiag} onEnsure={async () => {
                try {
                  const r = await api.post("/admin/diagnostics/stripe/ensure-catalog");
                  const c = r.data.created || [];
                  setNotice({ kind: "success", text: c.length
                    ? `Stripe catalog (${r.data.mode}): created ${c.join(", ")}.`
                    : `Stripe catalog (${r.data.mode}): already complete, nothing created.` });
                  load();
                } catch (ex) {
                  setNotice({ kind: "error", text: ex?.response?.data?.detail || "Could not ensure Stripe catalog." });
                }
              }} />}
        </section>

        <section className="card-flat p-6" data-testid="diag-billing-companies">
          <div className="label-mini">receiving companies</div>
          <h2 className="font-display text-xl font-semibold mt-1">Free tier &amp; cards</h2>
          {!billCos ? <div className="text-sm text-neutral-500 mt-4">Loading…</div> : (
            <BillingCompanies data={billCos} onRemoved={(text, isErr) => {
              setNotice({ kind: isErr ? "error" : "success", text });
              if (!isErr) load();
            }} />
          )}
        </section>

        {/* ---- Hosting ---- */}
        <section className="card-flat p-6" data-testid="diag-hosting-card">
          <div className="label-mini flex items-center gap-2">
            <Server size={12} /> hosting
          </div>
          <h2 className="font-display text-xl font-semibold mt-1">Backend outbound</h2>
          {hosting ? (
            <div className="mt-4 text-sm space-y-2">
              <KV k="ipify" v={hosting.ipify || "—"} mono />
              <KV k="ifconfig.me" v={hosting.ifconfig_me || "—"} mono />
              <KV k="hostname" v={hosting.hostname || "—"} mono />
              <KV k="region" v={hosting.region || "—"} />
              <KV k="checked at" v={hosting.checked_at ? new Date(hosting.checked_at).toLocaleString() : "—"} />
            </div>
          ) : <div className="text-sm text-neutral-500 mt-4">Loading…</div>}
        </section>

        {/* ---- QBO API log ---- */}
        <section className="card-flat p-6" data-testid="diag-qbo-log-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="label-mini flex items-center gap-2"><Globe size={12} /> quickbooks api log</div>
              <h2 className="font-display text-xl font-semibold mt-1">Recent QuickBooks calls</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Every QuickBooks HTTP call. 90-day retention. Shows intuit_tid + Fault code when something fails.
              </p>
            </div>
            <button className="btn-primary flex items-center gap-2 text-xs"
                    data-testid="diag-qbo-log-csv-btn"
                    onClick={downloadCsv}>
              <Download size={12} /> csv
            </button>
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={errorsOnly}
                     onChange={(e) => setErrorsOnly(e.target.checked)}
                     data-testid="diag-qbo-log-errors-only" />
              errors only
            </label>
            <input type="text" placeholder="filter by LF company id"
                   value={companyFilter}
                   onChange={(e) => setCompanyFilter(e.target.value)}
                   className="input text-xs"
                   data-testid="diag-qbo-log-company-filter"
                   style={{ width: "22rem" }} />
          </div>
          <div className="mt-4 border border-neutral-200">
            {log.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-neutral-500"
                   data-testid="diag-qbo-log-empty">
                No QuickBooks calls match the current filter.
              </div>
            ) : log.map((r) => (
              <div key={r.id} className="px-4 py-3 border-b border-neutral-100 text-sm"
                   data-testid={`diag-qbo-log-row-${r.id}`}>
                <div className="flex items-center justify-between gap-4 font-mono text-xs text-neutral-500">
                  <span>{new Date(r.created_at).toLocaleString()}</span>
                  <span className={r.status >= 400 ? "text-red-700" : "text-emerald-700"}>
                    {r.method} · {r.status} · {r.duration_ms}ms · {r.kind}
                  </span>
                </div>
                <div className="mt-1 font-mono text-xs break-all">{r.endpoint}</div>
                <div className="mt-1 font-mono text-xs text-neutral-600">
                  realm: {r.realm_id || "—"} · intuit_tid: {r.intuit_tid || "—"}
                </div>
                {r.error_code || r.error_message ? (
                  <div className="mt-1 text-xs text-red-700">
                    error {r.error_code ? `[${r.error_code}] ` : ""}{r.error_message || ""}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </section>
        <DiagnosticsSecurity />
      </div>
    </Layout>
  );
}

function KV({ k, v, mono = false }) {
  return (
    <div className="flex items-start gap-3">
      <div className="label-mini w-40 shrink-0">{k}</div>
      <div className={mono ? "font-mono text-xs text-neutral-800" : "text-sm text-neutral-800"}>{v}</div>
    </div>
  );
}


function Flag({ v }) {
  const good = v === "set" || v === "ok" || v === true;
  const text = v === true ? "yes" : v === false ? "no" : v ?? "—";
  return <span className={good ? "text-emerald-700" : "text-amber-700"}>{text}</span>;
}

function StripeBlock({ d, onEnsure }) {
  const w = d.last_webhook;
  return (
    <div className="mt-4 border border-neutral-200 px-4 py-3 text-sm" data-testid="diag-stripe">
      <div className="font-medium">stripe · <span data-testid="diag-stripe-mode">{d.mode}</span> mode</div>
      <div className="text-xs mt-1" data-testid="diag-stripe-keys">
        STRIPE_SECRET_KEY <Flag v={d.secret_key} />
        {" · "}STRIPE_PUBLISHABLE_KEY <Flag v={d.publishable_key} />
        {" · "}STRIPE_WEBHOOK_SECRET <Flag v={d.webhook_secret} />
      </div>
      <div className="text-xs text-neutral-600 font-mono mt-1" data-testid="diag-stripe-webhook-url">webhook: {d.webhook_url}</div>
      <div className="text-xs mt-1" data-testid="diag-stripe-ping">
        api ping <Flag v={d.api_ping} />
        {" · "}$0.25 metered price resolves <Flag v={d.price_resolves} />
        {d.price_id ? <span className="font-mono text-neutral-600"> ({d.price_id})</span> : null}
      </div>
      <div className="text-xs text-neutral-600 mt-1" data-testid="diag-stripe-customers">
        companies with a Stripe customer: {d.companies_with_customer} / {d.companies_total}
      </div>
      <div className="text-xs text-neutral-600 font-mono mt-1" data-testid="diag-stripe-last-webhook">
        last webhook: {w ? `${w.at} · ${w.type} · ${w.result}` : "none received"}
      </div>
      {d.secret_key === "set" ? (
        <button type="button" className="btn-outline text-xs mt-3" onClick={onEnsure}
                data-testid="diag-stripe-ensure-catalog">
          ensure stripe catalog
        </button>
      ) : null}
    </div>
  );
}


function BillingCompanies({ data, onRemoved }) {
  const [busy, setBusy] = useState(null);
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.company_name}? Any carried-forward balance is billed once, its users are signed out and its connections stop syncing.`)) return;
    setBusy(c.company_id);
    try {
      const r = await api.delete(`/admin/companies/${c.company_id}`);
      onRemoved(`${c.company_name} removed · final bill: ${r.data.final_bill?.state || "none"}`);
    } catch (e) {
      onRemoved(e?.response?.data?.detail || "Could not remove company", true);
    } finally {
      setBusy(null);
    }
  };
  if (data.error) return <div className="text-sm text-red-700 mt-4">Could not load.</div>;
  if (!data.companies.length) return <div className="text-sm text-neutral-500 mt-4">No receiving companies yet.</div>;
  return (
    <div className="overflow-x-auto mt-4">
      <table className="w-full text-xs">
        <thead><tr className="text-left label-mini">
          <th className="py-2">company</th><th>free tier start</th><th>received</th>
          <th>free tier</th><th>card on file</th><th>held for billing</th><th>carried forward</th><th></th>
        </tr></thead>
        <tbody>
          {data.companies.map((c) => (
            <tr key={c.company_id} className="border-t border-neutral-200" data-testid={`diag-billco-${c.company_id}`}>
              <td className="py-2">{c.company_name}</td>
              <td className="font-mono">{(c.free_tier_start || "—").slice(0, 10)}</td>
              <td>{c.invoices_received}</td>
              <td>{c.free_tier}</td>
              <td>{c.card_on_file ? "yes" : "no"}</td>
              <td className={c.held_for_billing ? "text-amber-700 font-medium" : ""}>{c.held_for_billing}</td>
              <td className="font-mono" data-testid={`diag-billco-carried-${c.company_id}`}>
                ${(c.carried_forward_usd || 0).toFixed(2)}
              </td>
              <td className="text-right">
                <button type="button" className="underline text-red-700 disabled:opacity-50"
                        disabled={busy === c.company_id} onClick={() => remove(c)}
                        data-testid={`diag-billco-remove-${c.company_id}`}>
                  {busy === c.company_id ? "removing…" : "remove company"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-xs text-neutral-500 mt-2">showing {data.companies.length} of {data.total}</div>
    </div>
  );
}

